import { api } from './api';

export interface Saida {
  id: number;
  idSaida?: number;
  idRetorno: number | null;
  praca: string;
  vendedor: string;
  textoExibir?: string;
  dataSaidaString: string;
  dataRetornoString?: string;
  dataPrevistaRetornoString: string;
}

export interface SaidaDetalhes {
  id: number;
  idRetorno: number | null;
  idRomaneio?: number;
  idPraca?: number;
  idVendedor?: number;
  praca: string;
  vendedor: string;
  nomePromotor?: string;
  nomeVendedor?: string;
  cpf?: string;
  telefone?: string;
  endereco?: string;
  dataSaida: string;
  dataSaidaString: string;
  dataRetorno?: string;
  dataRetornoString?: string;
  dataPrevistaRetorno: string;
  dataPrevistaRetornoString: string;
  produtos?: ProdutoSaida[];
  produtosResumidos?: ProdutoSaidaResumido[];
}

export interface ProdutoSaida {
  id: number;
  idSaida: number;
  idProduto: number;
  nomeProduto: string;
  referencia: string;
  quantidade: number;
  precoVenda: string;
  valorTotal: number;
  codigoBarras?: string | string[];
}

export interface ProdutoSaidaResumido {
  id: number;
  idProduto: number;
  nomeProduto: string;
  referencia: string;
  quantidade: number;
  precoVenda: string;
  semComissao: number | boolean;
  quantidadeDevolvida?: number;
}

export interface DetalhesRetorno {
  id: number;
  idVendedor: number;
  funcionario: string;
  praca: string;
  cpf: string;
  vendedor: string;
  dataSaida: string;
  telefone: string;
  endereco: string;
  numero: string;
  bairro: string;
  cidade: string;
  estado: string;
  valorTotalMercadoriaComComissao: number;
  valorTotalMercadoriaSemComissao: number;
  produtosResumidos: ProdutoSaidaResumido[];
}

export interface ProdutoViewRetorno {
  idProduto: number;
  codigoBarras: string;
  referencia: string;
  nomeProduto: string;
  precoVenda: number;
}

export interface ProdutoResumoRetorno {
  id: number;
  idProduto: number;
  referencia: string;
  nomeProduto: string;
  precoVenda: number;
  quantidadeVendida: number;
  quantidadeRetorno: number;
}

export interface PagamentoItemView {
  id: number;
  idPagamento: number;
  valorPago: number;
  formaPagamento: string;
  dataPagamento: string;
  dataInclusao: string;
  dataPagamentoString: string;
  dataInclusaoString: string;
}

export interface PagamentoView {
  id: number;
  idRetorno: number;
  totalDaVenda: number;
  percentualVendedora: number;
  valorASerPago: number;
  totalPago: number;
  saldo: number;
  pagamentosItens: PagamentoItemView[];
}

export interface ViewRetorno {
  id: number;
  idSaida: number;
  idVendedor: number;
  nomePromotor: string;
  praca: string;
  nomeVendedor: string;
  cpf: string;
  dataSaida: string;
  dataRetorno: string;
  dataSaidaString: string;
  dataRetornoString: string;
  idPraca: number;
  produtos: ProdutoViewRetorno[];
  produtosResumidos: ProdutoResumoRetorno[];
  pagamento: PagamentoView;
}

export interface RetornoRequest {
  IdSaida: number;
  DataRetorno: string;
  IdVendedor: number;
  TotalVenda: number;
  ValorASerPago: number;
  TotalPago: number;
  PercentualVendedor: number;
  ProdutosDevolvidos: ProdutoSaidaResumido[];
  Pagamentos: {
    ValorPago: number;
    IdFormaPagamento: number;
    DataPagamento: string;
  }[];
}

export interface SaidaRequest {
  idVendedor: number;
  idPraca: number;
  dataSaida: string;
  dataPrevistaRetorno: string;
  produtos: ProdutoSaida[];
}

export const saidaService = {
  getAll: async (): Promise<Saida[]> => {
    const response = await api.get<Saida[]>('/api/Saida/GetAllSaidasTable');
    return response.data;
  },

  getAllComFiltro: async (filtros: {
    dataSaidaInicio?: string;
    dataSaidaFim?: string;
    dataRetornoInicio?: string;
    dataRetornoFim?: string;
    idPraca?: number;
    idVendedor?: number;
    idPromotor?: number;
  }): Promise<Saida[]> => {
    const params = new URLSearchParams();
    if (filtros.dataSaidaInicio) params.append('dataSaidaInicio', filtros.dataSaidaInicio);
    if (filtros.dataSaidaFim) params.append('dataSaidaFim', filtros.dataSaidaFim);
    if (filtros.dataRetornoInicio) params.append('dataRetornoInicio', filtros.dataRetornoInicio);
    if (filtros.dataRetornoFim) params.append('dataRetornoFim', filtros.dataRetornoFim);
    if (filtros.idPraca) params.append('idPraca', filtros.idPraca.toString());
    if (filtros.idVendedor) params.append('idVendedor', filtros.idVendedor.toString());
    if (filtros.idPromotor) params.append('idPromotor', filtros.idPromotor.toString());

    const response = await api.get<Saida[]>(`/api/Saida/GetAllSaidasTableComFiltro?${params.toString()}`);
    return response.data;
  },

  getById: async (id: number): Promise<SaidaDetalhes> => {
    const response = await api.get<SaidaDetalhes>(`/api/Saida/GetById?id=${id}`);
    return response.data;
  },

  getByIdResumido: async (id: number): Promise<SaidaDetalhes> => {
    const response = await api.get<SaidaDetalhes>(`/api/Saida/GetByIdResumido?id=${id}`);
    return response.data;
  },

  salvar: async (saida: SaidaRequest): Promise<SaidaDetalhes> => {
    const response = await api.post<SaidaDetalhes>('/api/Saida/Salvar', saida);
    return response.data;
  },

  adicionarProdutos: async (saida: any): Promise<SaidaDetalhes> => {
    const response = await api.post<SaidaDetalhes>('/api/Saida/SalvarNovosProdutos', saida);
    return response.data;
  },

  salvarAlteracaoDeDatas: async (data: {
    idSaida: number;
    dataSaidaAnterior: string;
    dataRetornoAnterior: string;
    idVendedorAnterior: number;
    novaDataNotaSaida: string;
    novaDataNotaRetorno: string;
    idVendedor: number;
  }): Promise<any> => {
    const response = await api.post('/api/Saida/SalvarAlteracaoDeDatas', data);
    return response.data;
  },

  salvarUnificar: async (idSaidaOriginal: number, idSaidaUnificar: number): Promise<any> => {
    const response = await api.post('/api/Saida/SalvarUnificar', {
      idSaidaOriginal,
      idSaidaUnificar,
    });
    return response.data;
  },

  getSaidasDoVendedor: async (id: number): Promise<Saida[]> => {
    const response = await api.get<Saida[]>(`/api/Saida/GetSaidasDoVendedor?id=${id}`);
    return response.data;
  },

  buscarProdutoNaSaida: async (idSaida: number, codigoBarra: string): Promise<ProdutoSaida> => {
    const response = await api.get<ProdutoSaida>(
      `/api/Saida/BuscarProdutoPorCodigoBarraNaSaida?idSaida=${idSaida}&codigoBarra=${codigoBarra}`
    );
    return response.data;
  },

  buscarDetalhesRetorno: async (id: number): Promise<DetalhesRetorno> => {
    const response = await api.get<DetalhesRetorno>(`/api/Saida/GetDetalhesRetorno?idSaida=${id}`);
    return response.data;
  },
};

export const retornoService = {
  salvar: async (retorno: RetornoRequest): Promise<any> => {
    const response = await api.post('/api/Retorno/Salvar', retorno);
    return response.data;
  },

  devolverTodosItens: async (idNotaSaida: number): Promise<any> => {
    const response = await api.post(`/api/Retorno/DevolverTodosItens/${idNotaSaida}`);
    return response.data;
  },

  buscarDetalhesRetorno: async (id: number): Promise<ViewRetorno> => {
    const response = await api.get<ViewRetorno>(`/api/Retorno/GetVisualizacaoRetorno?id=${id}`);
    return response.data;
  },

  excluirNotaRetorno: async (id: number): Promise<any> => {
    const response = await api.post(`/api/Retorno/ExcluirNotaRetorno/${id}`);
    return response.data;
  },
};
